import React, { FC, useCallback, useEffect, useState } from 'react';
import { Box, Button, Chip, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useConnection } from '../../connection/Application';
import { readBody } from './cbaApi';
import { formatAttemptDate, formatTimeUsed } from './resultView';
import {
  AttemptRecord,
  changeFromPrevious,
  changeLabel,
  closedLabel,
  newestFirst,
} from './attemptHistoryView';

/**
 * Every completed sitting of one paper, newest first.
 *
 * A student can sit the same mock as often as they like, so a list of scores is
 * only useful if it shows movement: each row carries how it compares with the
 * sitting before it.
 */
export const AttemptHistory: FC<{ paperId: string }> = ({ paperId }) => {
  const connection = useConnection();
  const navigate = useNavigate();

  const [attempts, setAttempts] = useState<AttemptRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const fetchAttempts = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const response = await connection.get(`cba/papers/${paperId}/attempts`);
      setAttempts(readBody<AttemptRecord[]>(response) ?? []);
    } catch (e) {
      setAttempts([]);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [connection, paperId]);

  useEffect(() => {
    fetchAttempts();
  }, [fetchAttempts]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  // A student who has never sat the paper needs no empty table, and a list that
  // failed to load should not be mistaken for one.
  if (failed) {
    return (
      <Typography variant='body2' sx={{ color: 'text.secondary' }}>
        Your previous attempts could not be loaded.
      </Typography>
    );
  }

  if (attempts.length === 0) {
    return null;
  }

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size='small' aria-label='Your previous attempts'>
        <TableHead>
          <TableRow>
            <TableCell>Attempt</TableCell>
            <TableCell>Score</TableCell>
            <TableCell>Percentage</TableCell>
            <TableCell>Time used</TableCell>
            <TableCell>Date</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {newestFirst(attempts).map((attempt) => {
            const change = changeFromPrevious(attempts, attempt);

            return (
              <TableRow key={attempt.attemptId}>
                <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {attempt.attemptNo}
                  {attempt.status === 'expired' && (
                    <Chip size='small' label={closedLabel(attempt)} sx={{ ml: 1 }} variant='outlined' />
                  )}
                </TableCell>
                <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {attempt.score} / {attempt.totalMarks}
                </TableCell>
                <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {Math.round(attempt.percentage * 100) / 100}%
                  {change !== undefined && change !== 0 && (
                    <Typography
                      component='span'
                      variant='caption'
                      sx={{ ml: 1, color: change > 0 ? '#0F5C55' : '#A32A1E', fontWeight: 600 }}>
                      {changeLabel(change)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{formatTimeUsed(attempt.timeUsedSeconds)}</TableCell>
                <TableCell>{formatAttemptDate(attempt.submittedAt)}</TableCell>
                <TableCell align='right'>
                  <Button
                    size='small'
                    onClick={() => navigate(`/cba/attempts/${attempt.attemptId}/review`)}>
                    Review
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};

export default AttemptHistory;
